import React, { Component } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import { Helmet } from 'react-helmet';
import './timeline.css';
import ConfirmDialog from './ConfirmDialog';

const LOAD_SIZE = 50;
const PAGE_SIZE = 10;

const RESTRICT_OPTIONS = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
  { label: '30 days', hours: 720 },
];

class Timeline extends Component {
  constructor(props) {
    super(props);
    this.state = {
      posts: [],
      loading: true,
      loadingMore: false,
      hasMore: false,
      lastDoc: null,
      barInfo: null,
      authorNames: {},
      authorAvatars: {},
      authorEmails: {},
      blockedUidSet: new Set(),
      confirm: null,
      restrictMenu: null,
      search: '',
      filter: 'all',
      page: 0,
    };
  }

  componentDidMount() {
    const data = localStorage.getItem('info');
    const barInfo = data ? JSON.parse(data) : null;
    this.setState({ barInfo }, () => {
      if (barInfo) {
        this.loadPosts();
        this.loadBlocked();
      }
    });
  }

  loadBlocked() {
    const { barInfo } = this.state;
    firebase.firestore()
      .collection('venueBlocks')
      .where('venueId', '==', barInfo.id)
      .get()
      .then(snap => {
        const blockedUidSet = new Set(
          snap.docs.map(d => d.data().blockedUid).filter(Boolean)
        );
        this.setState({ blockedUidSet });
      })
      .catch(() => {});
  }

  loadPosts() {
    const { barInfo } = this.state;
    firebase.firestore()
      .collection('posts')
      .where('venueId', '==', barInfo.id)
      .where('visibility', '==', 'public')
      .where('platformRemoved', '==', false)
      .orderBy('createdAt', 'desc')
      .limit(LOAD_SIZE)
      .get()
      .then(snap => {
        const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const lastDoc = snap.docs[snap.docs.length - 1] || null;
        this.setState({ posts, loading: false, lastDoc, hasMore: snap.docs.length === LOAD_SIZE });
        const uids = [...new Set(posts.map(p => p.authorUid).filter(Boolean))];
        uids.forEach(uid => this.loadAuthor(uid));
      })
      .catch(() => this.setState({ loading: false }));
  }

  loadMore() {
    const { barInfo, lastDoc, posts } = this.state;
    if (!lastDoc) return;
    this.setState({ loadingMore: true });
    firebase.firestore()
      .collection('posts')
      .where('venueId', '==', barInfo.id)
      .where('visibility', '==', 'public')
      .where('platformRemoved', '==', false)
      .orderBy('createdAt', 'desc')
      .startAfter(lastDoc)
      .limit(LOAD_SIZE)
      .get()
      .then(snap => {
        const newPosts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const newLastDoc = snap.docs[snap.docs.length - 1] || null;
        this.setState(prev => ({
          posts: [...prev.posts, ...newPosts],
          lastDoc: newLastDoc,
          hasMore: snap.docs.length === LOAD_SIZE,
          loadingMore: false,
        }));
        const uids = [...new Set(newPosts.map(p => p.authorUid).filter(Boolean))];
        uids.forEach(uid => this.loadAuthor(uid));
      })
      .catch(() => this.setState({ loadingMore: false }));
  }

  loadAuthor(uid) {
    firebase.firestore()
      .collection('users')
      .doc(uid)
      .get()
      .then(snap => {
        if (!snap.exists) return;
        const d = snap.data();
        this.setState(prev => ({
          authorNames: { ...prev.authorNames, [uid]: d.name || d.username || d.email || uid },
          authorAvatars: { ...prev.authorAvatars, [uid]: d.profileImageUrl || null },
          authorEmails: { ...prev.authorEmails, [uid]: d.email || null },
        }));
      })
      .catch(() => {});
  }

  removePost(postId) {
    this.setState({ confirm: { type: 'removePost', postId }, restrictMenu: null });
  }

  doRemovePost(postId) {
    this.setState({ confirm: null });
    firebase.firestore().collection('posts').doc(postId)
      .update({ removedFromVenue: true })
      .then(() => {
        this.setState(prev => ({
          posts: prev.posts.map(p =>
            p.id === postId ? { ...p, removedFromVenue: true } : p
          ),
        }));
      })
      .catch(err => {
        console.error('removePost failed:', err);
        alert('Could not remove post. Please try again.');
      });
  }

  removePhoto(postId, url) {
    firebase.firestore().collection('posts').doc(postId)
      .update({ removedPhotos: firebase.firestore.FieldValue.arrayUnion(url) })
      .then(() => {
        this.setState(prev => ({
          posts: prev.posts.map(p =>
            p.id === postId ? { ...p, removedPhotos: [...(p.removedPhotos || []), url] } : p
          ),
        }));
      })
      .catch(err => {
        console.error('removePhoto failed:', err);
        alert('Could not remove photo. Please try again.');
      });
  }

  blockUser(authorUid) {
    this.setState({ confirm: { type: 'blockUser', authorUid }, restrictMenu: null });
  }

  doBlockUser(authorUid) {
    this.setState({ confirm: null });
    const { barInfo } = this.state;
    const db = firebase.firestore();
    const actorUid = firebase.auth().currentUser?.uid || '';
    const batch = db.batch();
    batch.set(db.collection('venueBlocks').doc(`${barInfo.id}_${authorUid}`), {
      venueId: barInfo.id,
      blockedUid: authorUid,
      blockedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.set(db.collection('moderationLog').doc(), {
      type: 'venue_block',
      venueId: barInfo.id,
      targetUid: authorUid,
      actorUid,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.commit()
      .then(() => {
        this.setState(prev => {
          const blockedUidSet = new Set(prev.blockedUidSet);
          blockedUidSet.add(authorUid);
          return { blockedUidSet };
        });
      })
      .catch(() => alert('Could not block user. Please try again.'));
  }

  unblockUser(authorUid) {
    this.setState({ confirm: { type: 'unblockUser', authorUid }, restrictMenu: null });
  }

  doUnblockUser(authorUid) {
    this.setState({ confirm: null });
    const { barInfo } = this.state;
    firebase.firestore()
      .collection('venueBlocks')
      .doc(`${barInfo.id}_${authorUid}`)
      .delete()
      .then(() => {
        this.setState(prev => {
          const blockedUidSet = new Set(prev.blockedUidSet);
          blockedUidSet.delete(authorUid);
          return { blockedUidSet };
        });
      })
      .catch(() => alert('Could not unblock user. Please try again.'));
  }

  restrictUser(authorUid, hours) {
    this.setState({
      confirm: { type: 'restrictUser', authorUid, restrictHours: hours },
      restrictMenu: null,
    });
  }

  doRestrictUser(authorUid, hours) {
    this.setState({ confirm: null });
    const { barInfo } = this.state;
    const db = firebase.firestore();
    const actorUid = firebase.auth().currentUser?.uid || '';
    const until = new Date(Date.now() + hours * 3600 * 1000);
    const batch = db.batch();
    batch.set(db.collection('venueRestrictions').doc(`${barInfo.id}_${authorUid}`), {
      venueId: barInfo.id,
      restrictedUid: authorUid,
      until: firebase.firestore.Timestamp.fromDate(until),
      restrictedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.set(db.collection('moderationLog').doc(), {
      type: 'venue_restrict',
      venueId: barInfo.id,
      targetUid: authorUid,
      actorUid,
      hours,
      until: firebase.firestore.Timestamp.fromDate(until),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.commit()
      .catch(() => alert('Could not restrict user. Please try again.'));
  }

  reportPost(postId) {
    this.setState({ confirm: { type: 'reportPost', postId }, restrictMenu: null });
  }

  doReportPost(postId) {
    this.setState({ confirm: null });
    const actorUid = firebase.auth().currentUser?.uid || '';
    firebase.firestore().collection('reports').add({
      postId,
      reporterUid: actorUid,
      reason: 'Reported by venue owner',
      status: 'pending',
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    }).catch(() => alert('Could not report post. Please try again.'));
  }

  timeAgo(ts) {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  getFilteredPosts() {
    const { posts, search, filter, authorNames } = this.state;
    return posts.filter(post => {
      const isRemoved = post.removedFromVenue || post.platformRemoved;
      if (filter === 'active' && isRemoved) return false;
      if (filter === 'removed' && !isRemoved) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const textMatch = (post.text || '').toLowerCase().includes(q);
        const nameMatch = (authorNames[post.authorUid] || '').toLowerCase().includes(q) ||
        (this.state.authorEmails[post.authorUid] || '').toLowerCase().includes(q);
        if (!textMatch && !nameMatch) return false;
      }
      return true;
    });
  }

  renderPost(post) {
    const { authorNames, authorAvatars, authorEmails, blockedUidSet, restrictMenu } = this.state;
    const isRemoved = post.removedFromVenue || post.platformRemoved;
    const isBlocked = blockedUidSet.has(post.authorUid);
    const name = authorNames[post.authorUid] || 'Unknown';
    const email = authorEmails[post.authorUid] || null;
    const avatar = authorAvatars[post.authorUid];
    const initials = (name[0] || '?').toUpperCase();

    return (
      <div key={post.id} className={`tl-post${isRemoved ? ' removed' : ''}`}>
        <div className="tl-post-header">
          <div className="tl-avatar">
            {avatar
              ? <img src={avatar} alt="" />
              : initials
            }
          </div>
          <div className="tl-author-info">
            <span className="tl-author">{name}</span>
            {email && <span className="tl-email">{email}</span>}
            <span className="tl-time">{this.timeAgo(post.createdAt)}</span>
          </div>
          <div className="tl-badges">
            {isRemoved && <span className="tl-removed-badge">Removed</span>}
            {isBlocked && <span className="tl-blocked-badge">Blocked</span>}
          </div>
        </div>

        {post.text && <p className="tl-text">{post.text}</p>}

        {(() => {
          const hidden = post.removedPhotos || [];
          const visible = (post.photos || []).filter(url => !hidden.includes(url));
          return visible.length > 0 && (
            <div className="tl-photos">
              {visible.slice(0, 6).map((url, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={url} className="tl-photo" alt="" />
                  <button
                    type="button"
                    title="Remove this photo from the venue timeline"
                    onClick={() => this.removePhoto(post.id, url)}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer' }}
                  >×</button>
                </div>
              ))}
            </div>
          );
        })()}

        <hr className="tl-divider" />

        <div className="tl-actions" onClick={e => e.stopPropagation()}>
          {!isRemoved && (
            <button
              className="tl-btn danger"
              onClick={() => this.removePost(post.id)}
            >
              Remove post
            </button>
          )}

          {isBlocked ? (
            <button
              className="tl-btn"
              onClick={() => this.unblockUser(post.authorUid)}
            >
              Unblock user
            </button>
          ) : (
            <button
              className="tl-btn danger"
              onClick={() => this.blockUser(post.authorUid)}
            >
              Block user
            </button>
          )}

          <div className="tl-restrict-wrap">
            <button
              className="tl-btn warning"
              onClick={() => this.setState(prev => ({
                restrictMenu: prev.restrictMenu === post.id ? null : post.id,
              }))}
            >
              Restrict ▾
            </button>
            {restrictMenu === post.id && (
              <div className="tl-restrict-menu">
                {RESTRICT_OPTIONS.map(opt => (
                  <button
                    key={opt.hours}
                    className="tl-restrict-item"
                    onClick={() => this.restrictUser(post.authorUid, opt.hours)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="tl-btn"
            onClick={() => this.reportPost(post.id)}
          >
            Report
          </button>
        </div>
      </div>
    );
  }

  render() {
    const {
      posts, loading, loadingMore, hasMore,
      confirm, search, filter, page,
    } = this.state;

    const filtered = this.getFilteredPosts();
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const pagePosts = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

    const totalActive = posts.filter(p => !p.removedFromVenue && !p.platformRemoved).length;
    const totalRemoved = posts.length - totalActive;

    const confirmConfig = confirm && {
      removePost: {
        title: 'Remove post',
        message: "Remove this post from your venue timeline? It will stay on the user's personal timeline.",
        confirmLabel: 'Remove',
        onConfirm: () => this.doRemovePost(confirm.postId),
      },
      blockUser: {
        title: 'Block user',
        message: 'Block this user from posting on your venue timeline? They can still use BarZone elsewhere.',
        confirmLabel: 'Block',
        onConfirm: () => this.doBlockUser(confirm.authorUid),
      },
      unblockUser: {
        title: 'Unblock user',
        message: 'Allow this user to post on your venue timeline again?',
        confirmLabel: 'Unblock',
        onConfirm: () => this.doUnblockUser(confirm.authorUid),
      },
      restrictUser: {
        title: `Restrict user for ${RESTRICT_OPTIONS.find(o => o.hours === confirm.restrictHours)?.label || ''}`,
        message: 'This user will be unable to post on your venue timeline for the selected period.',
        confirmLabel: 'Restrict',
        onConfirm: () => this.doRestrictUser(confirm.authorUid, confirm.restrictHours),
      },
      reportPost: {
        title: 'Report post to BarZone',
        message: 'Flag this post for review by the BarZone team.',
        confirmLabel: 'Report',
        onConfirm: () => this.doReportPost(confirm.postId),
      },
    }[confirm?.type];

    return (
      <div
        className="tl-page"
        onClick={() => this.setState({ restrictMenu: null })}
      >
        <Helmet>
          <style>{'body { background-color: #F7F7F7 !important; }'}</style>
        </Helmet>

        {/* ── Header ── */}
        <div className="tl-header">
          <p className="tl-title">Venue Timeline</p>
          {!loading && (
            <div className="tl-stats">
              <span className="tl-stat">Total: {posts.length}</span>
              <span className="tl-stat stat-active">Active: {totalActive}</span>
              <span className="tl-stat stat-removed">Removed: {totalRemoved}</span>
            </div>
          )}
        </div>

        {/* ── Toolbar ── */}
        <div className="tl-toolbar">
          <div className="tl-search-wrap">
            <span className="tl-search-icon">🔍</span>
            <input
              className="tl-search"
              type="text"
              placeholder="Search by name, email or post content…"
              value={search}
              onChange={e => this.setState({ search: e.target.value, page: 0 })}
            />
          </div>
          <div className="tl-filter-tabs">
            {[
              { key: 'all', label: `All (${posts.length})` },
              { key: 'active', label: `Active (${totalActive})` },
              { key: 'removed', label: `Removed (${totalRemoved})` },
            ].map(f => (
              <button
                key={f.key}
                className={`tl-filter-btn${filter === f.key ? ' active' : ''}`}
                onClick={() => this.setState({ filter: f.key, page: 0 })}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="tl-loading">
            <div className="tl-spinner" />
            <p style={{ color: '#888', margin: 0 }}>Loading posts…</p>
          </div>
        )}

        {/* ── Empty ── */}
        {!loading && filtered.length === 0 && (
          <p className="tl-empty">
            {search || filter !== 'all'
              ? 'No posts match your search or filter.'
              : 'No posts yet for this venue.'}
          </p>
        )}

        {/* ── Result count ── */}
        {!loading && filtered.length > 0 && (
          <p className="tl-result-count">
            Showing {pagePosts.length} of {filtered.length} post{filtered.length !== 1 ? 's' : ''}
            {posts.length > filtered.length ? ` (filtered from ${posts.length} loaded)` : ''}
          </p>
        )}

        {/* ── Posts ── */}
        {pagePosts.map(post => this.renderPost(post))}

        {/* ── Load more from server ── */}
        {!loading && hasMore && (
          <div className="tl-load-more-wrap">
            <button
              className="tl-load-more"
              disabled={loadingMore}
              onClick={() => this.loadMore()}
            >
              {loadingMore ? 'Loading…' : `Load more posts`}
            </button>
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="tl-pagination">
            <button
              className="tl-page-btn"
              disabled={safePage === 0}
              onClick={() => this.setState({ page: safePage - 1 })}
            >
              ← Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => {
              if (totalPages <= 7 || Math.abs(i - safePage) <= 2 || i === 0 || i === totalPages - 1) {
                return (
                  <button
                    key={i}
                    className={`tl-page-btn${safePage === i ? ' current' : ''}`}
                    onClick={() => this.setState({ page: i })}
                  >
                    {i + 1}
                  </button>
                );
              }
              if (Math.abs(i - safePage) === 3) {
                return <span key={i} className="tl-page-info">…</span>;
              }
              return null;
            })}

            <button
              className="tl-page-btn"
              disabled={safePage === totalPages - 1}
              onClick={() => this.setState({ page: safePage + 1 })}
            >
              Next →
            </button>
          </div>
        )}

        {/* ── Confirm dialog ── */}
        {confirm && confirmConfig && (
          <ConfirmDialog
            open
            title={confirmConfig.title}
            message={confirmConfig.message}
            confirmLabel={confirmConfig.confirmLabel}
            onConfirm={confirmConfig.onConfirm}
            onCancel={() => this.setState({ confirm: null })}
          />
        )}
      </div>
    );
  }
}

export default Timeline;
